/**
 * Task Card Component
 * Individual task card with quick actions menu for GTD workflow
 */

import React, { useState } from "react";
import { Typography, Tooltip, Dropdown, message, Modal } from "antd";
import type { MenuProps } from "antd";
import {
  PhoneOutlined,
  MailOutlined,
  LaptopOutlined,
  TeamOutlined,
  HourglassOutlined,
  MoreOutlined,
  FireOutlined,
  ForwardOutlined,
  CalendarOutlined,
  ScheduleOutlined,
  CloudOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUpdate, useDelete } from "@refinedev/core";

const { Text } = Typography;

export interface TaskCardData {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  projectColor?: string;
  priority: "now" | "tomorrow" | "this_week" | "next_week" | "someday";
  status: string;
  context?: string;
  dueDate?: string;
}

export interface TaskCardProps {
  task: TaskCardData;
  compact?: boolean;
  onClick?: () => void;
  onUpdate?: () => void; // Callback after update
}

// Context icon mapping
const contextIcons: Record<string, React.ReactNode> = {
  "@phone": <PhoneOutlined />,
  "@email": <MailOutlined />,
  "@computer": <LaptopOutlined />,
  "@meeting": <TeamOutlined />,
};

// Priority colors
const priorityColors: Record<string, string> = {
  now: "#ff4d4f",
  tomorrow: "#faad14",
  this_week: "#1890ff",
  next_week: "#8c8c8c",
  someday: "#d9d9d9",
};

// Priority labels for menu
const priorityOptions = [
  {
    key: "now",
    label: "NOW",
    icon: <FireOutlined style={{ color: "#ff4d4f" }} />,
  },
  {
    key: "tomorrow",
    label: "Tomorrow",
    icon: <ForwardOutlined style={{ color: "#faad14" }} />,
  },
  {
    key: "this_week",
    label: "This Week",
    icon: <CalendarOutlined style={{ color: "#1890ff" }} />,
  },
  {
    key: "next_week",
    label: "Next Week+",
    icon: <ScheduleOutlined style={{ color: "#8c8c8c" }} />,
  },
  {
    key: "someday",
    label: "Someday",
    icon: <CloudOutlined style={{ color: "#bfbfbf" }} />,
  },
];

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  compact = false,
  onClick,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isWaiting = task.status === "Waiting";

  const { mutate: updateTask } = useUpdate();
  const { mutate: deleteTask } = useDelete();

  const handleClick = () => {
    // Don't navigate if clicking on menu
    if (isMenuOpen) return;

    if (onClick) {
      onClick();
    } else {
      navigate(`/project-tasks/${task.id}`);
    }
  };

  const handlePriorityChange = (newPriority: string) => {
    updateTask(
      {
        resource: "project-tasks",
        id: task.id,
        values: { projecttaskpriority: newPriority },
        mutationMode: "optimistic",
      },
      {
        onSuccess: () => {
          message.success(
            `Moved to ${
              priorityOptions.find((p) => p.key === newPriority)?.label
            }`
          );
          onUpdate?.();
        },
        onError: () => {
          message.error("Failed to update task");
        },
      }
    );
  };

  const handleStatusChange = (newStatus: string) => {
    updateTask(
      {
        resource: "project-tasks",
        id: task.id,
        values: { projecttaskstatus: newStatus },
        mutationMode: "optimistic",
      },
      {
        onSuccess: () => {
          if (newStatus === "Completed") {
            message.success("Task completed!");
          } else if (newStatus === "Waiting") {
            message.info("Task marked as waiting");
          } else {
            message.success(`Status: ${newStatus}`);
          }
          onUpdate?.();
        },
        onError: () => {
          message.error("Failed to update task");
        },
      }
    );
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Delete Task",
      content: `Are you sure you want to delete "${task.name}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        deleteTask(
          {
            resource: "project-tasks",
            id: task.id,
            mutationMode: "optimistic",
          },
          {
            onSuccess: () => {
              message.success("Task deleted");
              onUpdate?.();
            },
            onError: () => {
              message.error("Failed to delete task");
            },
          }
        );
      },
    });
  };

  const handleEdit = () => {
    navigate(`/project-tasks/${task.id}/edit`);
  };

  // Build menu items
  const menuItems: MenuProps["items"] = [
    {
      key: "priority-group",
      type: "group",
      label: "Move to",
      children: priorityOptions
        .filter((p) => p.key !== task.priority) // Don't show current priority
        .map((p) => ({
          key: `priority-${p.key}`,
          icon: p.icon,
          label: p.label,
          onClick: () => handlePriorityChange(p.key),
        })),
    },
    { type: "divider" },
    {
      key: "waiting",
      icon: <HourglassOutlined style={{ color: "#faad14" }} />,
      label: isWaiting ? "Resume (Open)" : "Waiting",
      onClick: () => handleStatusChange(isWaiting ? "Open" : "Waiting"),
    },
    { type: "divider" },
    {
      key: "complete",
      icon: <CheckOutlined style={{ color: "#52c41a" }} />,
      label: "Complete",
      onClick: () => handleStatusChange("Completed"),
    },
    {
      key: "delete",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      label: "Delete",
      danger: true,
      onClick: handleDelete,
    },
    { type: "divider" },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit...",
      onClick: handleEdit,
    },
  ];

  // Menu button component
  const MenuButton = (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 20,
        height: 20,
        borderRadius: 4,
        cursor: "pointer",
      }}
      className="task-menu-trigger"
    >
      <MoreOutlined style={{ fontSize: 14, color: "#8c8c8c" }} />
    </div>
  );

  // Compact view for partial panel
  if (compact) {
    return (
      <div
        style={{
          padding: "8px 12px",
          background: isWaiting ? "#fffbe6" : "#fff",
          borderLeft: `3px solid ${priorityColors[task.priority] || "#d9d9d9"}`,
          borderRadius: 4,
          marginBottom: 8,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          position: "relative",
        }}
        className="task-card-compact"
      >
        <div style={{ flex: 1, minWidth: 0 }} onClick={handleClick}>
          <Text
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {isWaiting && (
              <HourglassOutlined style={{ marginRight: 4, color: "#faad14" }} />
            )}
            {task.name}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {task.projectName}
          </Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {task.context && contextIcons[task.context] && (
            <Tooltip title={task.context}>
              <span style={{ color: "#722ed1" }}>
                {contextIcons[task.context]}
              </span>
            </Tooltip>
          )}
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
            onOpenChange={setIsMenuOpen}
          >
            {MenuButton}
          </Dropdown>
        </div>
      </div>
    );
  }

  // Full card for timeline board
  return (
    <div
      style={{
        padding: "4px 8px",
        background: isWaiting
          ? "#fffbe6"
          : priorityColors[task.priority] + "15",
        border: `1px solid ${priorityColors[task.priority] || "#d9d9d9"}`,
        borderRadius: 4,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        position: "relative",
      }}
      className="task-card-full"
    >
      {isWaiting && (
        <HourglassOutlined style={{ color: "#faad14", fontSize: 12 }} />
      )}
      <Tooltip title={task.name} placement="top">
        <Text
          onClick={handleClick}
          style={{
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
          }}
        >
          {task.name}
        </Text>
      </Tooltip>
      {task.context && contextIcons[task.context] && (
        <span style={{ color: "#722ed1", fontSize: 11 }}>
          {contextIcons[task.context]}
        </span>
      )}
      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        placement="bottomRight"
        onOpenChange={setIsMenuOpen}
      >
        {MenuButton}
      </Dropdown>
    </div>
  );
};
